// The workbook index at pane open (product owner, 2026-09-14): every sheet is captured whole and uploaded, the
// server builds the deterministic index at commit, then refines the structures with the model pass (the
// "understanding" phase). The composer stays locked through both phases, so a conversation starts on the index it
// keeps — and on the whole of it: the 120 s cap that used to unlock the chat mid-pass is gone (product owner,
// 2026-09-20), because the first question is usually about what a sheet holds, which is exactly what the pass is
// still writing. A conversation that starts on half-understood structures answers from the other half later.
// The only way out is a stall: after `pollMaxMs` the chat opens with `backgroundUnderstanding` set, rather than
// locking the pane on a pass that is never going to finish.
// Host-agnostic: the executor and the API are injected (mocked in tests).
//
// Identity (sculpt-api PLAN-workbook-identity.md): a file announced from a path its workbook does not know — a Save
// As, a move, a copy — gets the identity question instead of a job. It blocks: nothing is captured and the chat
// waits until the user answers (same file / new version / other workbook) or dismisses it (D6, D5).
import type { IdentityAnswer, IdentityQuestion, IndexJobStart, IndexJobStarted, IndexSheetPayload, IndexSheetValues, IndexStatus, IterativeCalculation } from '../../types/index';
import { isCellEditModeError } from '../excel/officeErrors';
import { gzipJson } from './indexPayload';

export type IndexPhase = 'idle' | 'probing' | 'cell_edit' | 'needs_consent' | 'identity' | 'capturing' | 'committing' | 'understanding' | 'ready' | 'failed';

export interface IndexState {
  phase: IndexPhase;
  /** Sheets captured and uploaded so far / announced. */
  done: number;
  total: number;
  current: string | null;
  status: IndexStatus | null;
  error: string | null;
  /** The chat may open: index built and the sheets understood (or the model pass stalled past `pollMaxMs`). */
  ready: boolean;
  /** The pass stalled and the chat was opened anyway: it may still be running on the server. */
  backgroundUnderstanding: boolean;
  /** Size of the workbook, measured before asking: what the estimate on the intro screen is built from.
   *  Null until `probe()` has looked, or when the host could not answer. */
  size: { sheets: number; cells: number } | null;
  /** The question the server asked instead of opening a job (phase `identity`). */
  identity: IdentityQuestion | null;
  startedAt: number | null;
  finishedAt: number | null;
}

export interface IndexSheetListing {
  name: string;
  position: number;
  visibility: string;
}

export interface IndexerDeps {
  api: {
    startIndexJob(workbookId: string, body: IndexJobStart): Promise<IndexJobStarted>;
    uploadIndexSheet(workbookId: string, jobId: string, sheet: string, body: Uint8Array | string, encoding: 'gzip' | null): Promise<{ received: number; expected: number }>;
    commitIndexJob(workbookId: string, jobId: string): Promise<IndexStatus>;
    getIndexStatus(workbookId: string): Promise<IndexStatus>;
    /** The answers to the identity question: the id to carry on under. */
    answerIdentity?(workbookId: string, answer: 'same' | 'fork' | 'other', body: { document_url: string; name: string }): Promise<{ workbook_id: string }>;
    resumeIdentity?(workbookId: string, body: { document_url: string; target: string }): Promise<{ workbook_id: string }>;
    /** Progress / failure reports for the server log (optional, never awaited on the critical path). */
    reportIndexEvent?(workbookId: string, jobId: string, body: { event: string; sheet?: string | null; ms?: number; cells?: number; bytes?: number; error?: string | null }): Promise<void>;
  };
  executor: {
    listSheetsForIndex(): Promise<{ workbookName: string; sheets: IndexSheetListing[] }>;
    /** The file's URL or path, when the host gives one (empty for a workbook that was never saved). */
    getDocumentUrl?(): Promise<string | null>;
    readSheetForIndex(sheet: string, position: number): Promise<IndexSheetPayload>;
    /** The values alone of a sheet the job names in `values_only`. Optional: without it the whole sheet is read. */
    readSheetValuesForIndex?(sheet: string, position: number, fingerprint: string | null): Promise<IndexSheetValues>;
    listDefinedNames(): Promise<Record<string, string>>;
    /** Excel's iterative calculation setting, sent with the job's start. Optional: without it, or when it cannot be
     *  read (null), the start carries none. */
    readIterativeCalculation?(): Promise<IterativeCalculation | null>;
    /** Per-sheet fingerprints announced with the job: the server keeps the sheets whose fingerprint it already
     *  holds and asks only for the others (partial re-index). Optional: without it every sheet is uploaded. */
    readSheetFingerprints?(): Promise<Record<string, string | null>>;
    /** Sheet count and used-range cells, without reading any of them: the estimate the intro screen shows. */
    measureWorkbook?(): Promise<{ sheets: number; cells: number }>;
    /** Resolves once the user leaves the cell they are typing in: Excel holds the call until then. */
    waitForCellEditEnd(): Promise<void>;
  };
  ensureWorkbookId(): Promise<string>;
  /** Re-point the file to the id the server answered with (it recognised the file by its path, or the user
   *  answered the identity question with a new version or another workbook). */
  adoptWorkbookId?(id: string): Promise<void>;
  /** Write the id into the file again; false when it cannot be written (a read-only file, D16). `ensureWorkbookId`
   *  only writes a missing id, so this is what tells a read-only file on every run. */
  writeWorkbookId?(id: string): Promise<boolean>;
  /** The workbook id already written in the file, WITHOUT creating one. `probe()` needs to ask the server
   *  what it knows before anything is written to the document. */
  readWorkbookId?(): Promise<string | null>;
  /** Polling cadence of the model pass, ms, from the first ask on (tests shorten it). Without it the loop asks
   *  after `POLL_FIRST_MS` and doubles the wait up to the version's cadence. */
  pollMs?: number;
  /** Stop waiting for the model pass after this many ms and open the chat anyway (a stalled pass, not a slow
   *  one: the largest client workbook understands 19 sheets in about three minutes). */
  pollMaxMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

const UPLOAD_ATTEMPTS = 3;
const POLL_MAX_MS = 10 * 60 * 1000;
/** Polling cadence of a first index's pass. */
const FIRST_INDEX_POLL_MS = 4000;
/** Polling cadence of a re-index's pass (version 2 and later). */
const REINDEX_POLL_MS = 1000;
/** The first wait of a pass, doubled at each ask up to the cadence. The commit answers before the server's pass has
 *  started, so a pass is always still to come when the loop begins — and on a small workbook it ends a fraction of a
 *  second later (an empty one in 0.2 s): a first ask after the full 4 s held the composer that long on nothing. */
const POLL_FIRST_MS = 250;
const INITIAL: IndexState = { phase: 'idle', done: 0, total: 0, current: null, status: null, error: null, ready: false, backgroundUnderstanding: false, size: null, identity: null, startedAt: null, finishedAt: null };
const understood = (s: IndexStatus): boolean => s.model_pass.state !== 'pending' && s.model_pass.state !== 'running';

export class WorkbookIndexer {
  private state: IndexState = INITIAL;
  //: Sheets the agent has written in since the last index committed. They are announced with no
  //: fingerprint, which the server reads as "upload it". The fingerprint covers every cell's formula or
  //: constant (it used to be a top-left sample, and three cells written at `company_asset!K242`, `K253`,
  //: `K254` came back unchanged from two re-indexes), but not what the index also reads and a write can
  //: change without touching them: number formats, styles, recalculated values.
  private written = new Set<string>();
  //: The user asked to read the workbook again (`reread`): the next run captures every sheet, fingerprints aside.
  private rereadAll = false;
  //: A background run asked for (a write, a change in Excel, a refused turn) and not started yet: the run in
  //: progress may already have read past it. At most one waits behind the running one (`queueRun`).
  private pendingRun = false;
  //: The agent is working on a turn (`setAgentWorking`): what is written or changed meanwhile waits, and goes in one
  //: run once it stops. A run after each write batch interleaved its Excel reads with the next batch's writes and
  //: committed a version per batch, each cancelling the catalog build of the one before.
  private agentWorking = false;
  private queued: Promise<void> | null = null;
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  //: The loop watching the server's pass on the version last committed or found running (`understand`): one per
  //: version, which a turn refused for that pass joins (`awaitUnderstood`) instead of polling beside it.
  private understanding: Promise<void> | null = null;
  private stopped = false;
  //: The sheets the running background job captures, and whether a change to one of them has made its result
  //: stale before it was committed (`markChanged`): the job is then dropped for the run queued behind it.
  private capturing: Set<string> | null = null;
  private superseded = false;
  private workbookId: string | null = null;
  //: What the identity question was asked about: the answer is posted for this id, path and name.
  private asked: { workbookId: string; documentUrl: string; name: string; background: boolean } | null = null;
  //: The model picked on the intro, sent with the job starts of the first index until one is opened: the server
  //: runs the model pass on it while the workbook has no conversation (sculpt-api `workbooks.model`).
  private picked: string | null = null;
  //: The wait for the user to leave the cell they are typing in, shared by everyone who needs Excel meanwhile.
  private cellEditEnd: Promise<void> | null = null;

  constructor(private readonly deps: IndexerDeps) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): IndexState {
    return this.state;
  }

  getWorkbookId(): string | null {
    return this.workbookId;
  }

  private set(patch: Partial<IndexState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  /** Index the whole workbook (or re-index it: same pipeline, a new version). Concurrent calls join the run,
   *  and a call after `stop()` revives a run that has not returned yet (React's development mode mounts twice,
   *  stopping and restarting the effect in the same tick).
   *
   *  `background`: capture and commit without locking the composer. The understanding phase already had this
   *  (version 2 and later run in the background), but capture and commit did not — so the re-index that
   *  follows the agent's own writes shut the composer with "Indexing your workbook…" the moment the user
   *  read what had just been done to their model. */
  start(opts: { background?: boolean; dismissed?: boolean; workbookId?: string } = {}): Promise<void> {
    this.stopped = false;
    if (this.running) return this.running;
    this.running = this.run(opts.background === true, opts.dismissed === true, opts.workbookId).finally(() => {
      this.running = null;
    });
    return this.running;
  }

  refresh(): Promise<void> {
    return this.start();
  }

  /** Re-index with the chat left open: progress shows in the status line under the header, not in the
   *  full-pane indexing view (a turn waiting for the rebuild, `ChatSession.refreshIndex`). */
  async refreshInBackground(): Promise<void> {
    this.pendingRun = true;
    await this.queueRun(true);     // a turn waits on it: it runs even while the session counts as working
    await this.settled();
  }

  /** The chat says whether the agent is working on a turn: streaming, or running the batch it sent. A batch or a
   *  Question waiting on the user is not working. While it works, writes and changes are only recorded; when it
   *  stops, they are caught up in one run. */
  setAgentWorking(working: boolean): void {
    if (working === this.agentWorking) return;
    this.agentWorking = working;
    if (!working && this.pendingRun) void this.queueRun();
  }

  /** What the pane does on open, instead of indexing the workbook behind the user's back.
   *
   *  Read-only: it asks the file for the id it already carries (never creating one) and the server what it
   *  holds under it. A workbook the server has never seen stops at `needs_consent` with the size measured,
   *  so the intro can say what indexing is for and roughly how long it will take; the user starts it. One
   *  the server already knows opens the chat straight away and catches up in the background, which is the
   *  same trade as after a write — the freshness gate refuses a turn on a stale index anyway. */
  async probe(): Promise<void> {
    if (this.running || this.state.phase === 'needs_consent' || this.state.phase === 'cell_edit') return;
    this.set({ ...INITIAL, phase: 'probing' });
    let workbookId: string | null = null;
    try {
      workbookId = await this.readWorkbookIdOutsideCellEdit();
    } catch (err) {
      console.warn('[agent-v2] could not read the workbook id', err);
      workbookId = null;
    }
    // Measured after the id: Excel is known to answer by now, so the estimate is not lost to a cell edit.
    const size = await this.deps.executor.measureWorkbook?.().catch(() => null) ?? null;
    this.set({ size });
    if (!workbookId) {
      // No id in the file: the server cannot know it, and writing one is part of indexing.
      this.set({ phase: 'needs_consent' });
      return;
    }
    this.workbookId = workbookId;
    let status: IndexStatus;
    try {
      status = await this.deps.api.getIndexStatus(workbookId);
    } catch {
      // The server is unreachable. Ask rather than guess: the intro's button is also the retry.
      this.set({ phase: 'needs_consent' });
      return;
    }
    if (status.state !== 'ready' || !status.version) {
      this.set({ phase: 'needs_consent', status });
      return;
    }
    this.set({ status });
    if (understood(status)) this.set({ phase: 'ready', ready: true, finishedAt: Date.now() });
    else this.set({ phase: 'understanding', ready: false });
    // Known workbook: open the chat and pick up whatever changed since, without locking anyone out.
    void this.start({ background: true }).catch(() => undefined);
  }

  /** The id the file carries, waiting out a cell edit. Opening the pane while a cell is being typed in made every
   *  Office.js call fail, and the failed read looked like a file with no id: an indexed workbook got the consent
   *  screen, and stayed on it after the edit ended. Nothing polls: a refused read waits for the edit to end, which
   *  Excel signals by answering a held call, and reads again once — again only if the user went back to typing. */
  private async readWorkbookIdOutsideCellEdit(): Promise<string | null> {
    for (;;) {
      try {
        const id = (await this.deps.readWorkbookId?.()) ?? null;
        if (this.state.phase === 'cell_edit') this.set({ phase: 'probing' });
        return id;
      } catch (err) {
        if (!isCellEditModeError(err)) throw err;
        if (this.state.phase !== 'cell_edit') this.set({ phase: 'cell_edit' });
        await this.waitForCellEditEnd();
      }
    }
  }

  /** One wait for the end of a cell edit, however many callers need Excel meanwhile. */
  private waitForCellEditEnd(): Promise<void> {
    this.cellEditEnd ??= this.deps.executor.waitForCellEditEnd().finally(() => {
      this.cellEditEnd = null;
    });
    return this.cellEditEnd;
  }

  /** The intro's button: index this workbook for the first time, with the pane held while it runs, on the
   *  model picked there (none when the organization allows only one). */
  consent(model: string | null = null): Promise<void> {
    this.picked = model;
    this.set({ phase: 'idle' });
    return this.start();
  }

  /** The sheets a batch has just written in: the next index captures them whatever their fingerprint says,
   *  and that index starts as soon as the agent stops working (`setAgentWorking`), once for the whole turn.
   *
   *  Nothing used to start it. `markWritten` only tagged the names and the rebuild waited for the pane to be
   *  opened again, so a turn that wrote 48 changes left the index describing the workbook as it was before
   *  them — and the next turn planned against rows that had moved. The re-index runs in the background (the
   *  composer is only held for the first version of a workbook), so the user can keep typing through it. */
  markWritten(sheets: Iterable<string>): Promise<void> {
    let marked = false;
    for (const name of sheets) {
      if (name) {
        this.written.add(name);
        marked = true;
      }
    }
    if (!marked) return Promise.resolve();
    this.pendingRun = true;
    return this.agentWorking ? Promise.resolve() : this.queueRun();
  }

  /** The user asks for the workbook to be read again, from the header's dot: every sheet is captured, whatever its
   *  fingerprint says — the one case the automatic refresh does not cover is what a fingerprint does not see
   *  (formats, styles). A background run: the chat stays open, the status line shows it. */
  reread(): Promise<void> {
    this.rereadAll = true;
    this.pendingRun = true;
    return this.queueRun(true);
  }

  /** The sheets the user changed in Excel (`ExcelToolExecutor.watchSheetChanges`, debounced by the pane): a
   *  catch-up started before the next message so it does not have to wait for it. `[]` = the sheet list changed.
   *  Ignored until the workbook has an index: changing a workbook is not agreeing to index it.
   *
   *  Unlike the agent's writes, the sheets are not forced: their fingerprints decide. Excel reports a cell edit
   *  committed with nothing changed (F2 then Enter on a formula, to read it) like any other, and forcing the
   *  sheet captured it again with every sheet reading it. The fingerprint covers what these events report
   *  (formulas and constants, rows inserted, hidden or sorted, the sheet list), so an unchanged workbook gets
   *  `up_to_date` from the server: nothing captured, and the status line left as it was. */
  markChanged(sheets: string[]): Promise<void> {
    const indexed = !!this.workbookId && (this.state.phase === 'ready' || this.state.phase === 'understanding'
      || this.state.phase === 'capturing' || this.state.phase === 'committing' || (this.state.phase === 'failed' && this.state.ready));
    if (!indexed) return Promise.resolve();
    // A change to a sheet the running job captures (or to the sheet list) makes that job's result stale: its
    // commit — about 4 s on the server, and a version — would be replaced by the next run anyway.
    if (this.capturing && (!sheets.length || sheets.some((n) => this.capturing!.has(n)))) this.superseded = true;
    this.pendingRun = true;
    // While the agent works, the change waits with its writes: the add-in's own sheet events (a sheet created,
    // rows hidden or sorted) carry no source to filter them by, and land here too.
    return this.agentWorking ? Promise.resolve() : this.queueRun();
  }

  /** The sheets whose formats the user changed (`ExcelToolExecutor.watchSheetChanges`, `onFormatChanged`): captured
   *  again whatever their fingerprint says, which covers no style, like the sheets the agent writes in. Ignored, like
   *  `markChanged`, until the workbook has an index, and held while the agent works. */
  markRestyled(sheets: string[]): Promise<void> {
    const named = sheets.filter(Boolean);
    if (!named.length) return Promise.resolve();
    const indexed = !!this.workbookId && (this.state.phase === 'ready' || this.state.phase === 'understanding'
      || this.state.phase === 'capturing' || this.state.phase === 'committing' || (this.state.phase === 'failed' && this.state.ready));
    if (!indexed) return Promise.resolve();
    for (const name of named) this.written.add(name);
    if (this.capturing && named.some((n) => this.capturing!.has(n))) this.superseded = true;
    this.pendingRun = true;
    return this.agentWorking ? Promise.resolve() : this.queueRun();
  }

  /** A background run for what has been asked so far: now when nothing runs, else once after the run in
   *  progress — whose capture may already be past the change — and never more than one waiting. The waiting one
   *  gives way to an agent that started working meanwhile, unless `force` (a turn waits on it). */
  private queueRun(force = false): Promise<void> {
    if (!this.running) return this.start({ background: true }).catch(() => undefined);
    this.queued ??= this.running.catch(() => undefined).then(() => {
      this.queued = null;
      return this.pendingRun && (force || !this.agentWorking) ? this.queueRun(force) : undefined;
    });
    return this.queued;
  }

  /** Resolves once no run is in progress and none is waiting. */
  async settled(): Promise<void> {
    while (this.running || this.queued) await (this.queued ?? this.running)!.catch(() => undefined);
  }

  /** Wait for the understanding again, with the composer locked meanwhile, without re-capturing anything: the
   *  pane's fallback when a turn refused for the server's pass (409 `index_not_understood`) was reported instead
   *  of waiting by itself. Locked first, asked after: the refusal already said the pass is running, and leaving
   *  the composer open while the status call is in flight invites the user to send the turn that was just refused. */
  async recheck(): Promise<void> {
    if (!this.workbookId || this.running) return;
    this.stopped = false;
    this.set({ phase: 'understanding', ready: false, backgroundUnderstanding: false });
    await this.awaitUnderstood();
    // Unlocked whatever the answer: a server that cannot be asked, or a pass that stalled, locks nobody out for good.
    if (!this.running && !this.state.ready) {
      this.set({ phase: 'ready', ready: true, backgroundUnderstanding: !!this.state.status && !understood(this.state.status) });
    }
  }

  /** Resolves once the server has finished understanding the version it serves — a turn refused for that pass
   *  (409 `index_not_understood`) waits here before it is sent again — with whether it has: false when the pass
   *  stalled past `pollMaxMs`, the server could not be asked, or the pane was stopped. Nothing is captured:
   *  nothing changed. A run in progress or waiting goes first, since its commit starts a pass of its own, and the
   *  loop already watching a pass is joined rather than doubled. */
  async awaitUnderstood(): Promise<boolean> {
    // Bounded: a run that started while the pass was asked about, or a newer version taking the loop over, is
    // waited for once more, not for ever.
    for (let round = 0; round < 3; round++) {
      await this.settled();
      const workbookId = this.workbookId;
      if (!workbookId || this.stopped) return false;
      if (!this.understanding) {
        // Nothing watching: what the pane holds is the last poll's answer, and the refusal says the server's is newer.
        let status: IndexStatus;
        try {
          status = await this.deps.api.getIndexStatus(workbookId);
        } catch {
          return false;
        }
        if (this.running) continue;   // a run started meanwhile: its commit is the version to wait for
        this.set({ status });
        if (understood(status)) {
          this.set({ phase: 'ready', ready: true, backgroundUnderstanding: false });
          return true;
        }
        this.watchUnderstanding(workbookId, status);
      }
      await this.understanding;
      if (this.running || this.queued || this.understanding) continue;
      const current = this.state.status;
      return !!current && understood(current);
    }
    return false;
  }

  stop(): void {
    this.stopped = true;
  }

  /** The user's answer to the identity question. `same`, `fork`, `other` and `resume` are posted, the id the server
   *  answers is written into the file when it differs, and the index starts again under it; `dismiss` starts it
   *  again under the announced id, recording nothing — the question comes back at the next open (D5). A failed
   *  answer keeps the card, with the error. */
  async answerIdentity(answer: IdentityAnswer): Promise<void> {
    const asked = this.asked;
    const question = this.state.identity;
    if (!asked || !question || this.running) return;
    let workbookId = asked.workbookId;
    if (answer !== 'dismiss') {
      try {
        const api = this.deps.api;
        const answered = answer === 'resume'
          ? await api.resumeIdentity!(asked.workbookId, { document_url: asked.documentUrl, target: question.resume!.workbook_id })
          : await api.answerIdentity!(asked.workbookId, answer, { document_url: asked.documentUrl, name: asked.name });
        if (answered.workbook_id && answered.workbook_id !== asked.workbookId) {
          workbookId = this.workbookId = answered.workbook_id;
          await this.deps.adoptWorkbookId?.(answered.workbook_id);
        }
      } catch (err) {
        this.set({ error: err instanceof Error ? err.message : String(err) });
        return;
      }
    }
    this.asked = null;
    this.set({ identity: null, error: null });
    // Under the answered id, not the file's: the answer stands for this session even if writing it failed.
    return this.start({ background: asked.background, dismissed: answer === 'dismiss', workbookId });
  }

  private async run(background = false, dismissed = false, answered?: string): Promise<void> {
    // A background run keeps the composer open throughout: the status line says what is happening, and the
    // server refuses a turn on an index that is not ready with a 409 the pane already handles.
    // A background run keeps what the line already says until the server asks for sheets: on a workbook it
    // already holds (`up_to_date`), switching to `capturing` up front flashed a progress bar for nothing.
    // Only from a settled index: after an identity answer or a failure there is nothing worth keeping on screen.
    const quiet = background && (this.state.phase === 'ready' || this.state.phase === 'understanding');
    if (quiet) this.set({ ready: true, done: 0, total: 0, current: null, error: null, startedAt: Date.now() });
    else this.set({ ...INITIAL, size: this.state.size, phase: 'capturing', ready: background, startedAt: Date.now() });
    // The sheets this run captures whatever their fingerprint says, taken from `written`: a sheet marked again
    // while it runs (typed in after its read) goes back into `written` for the next run. Given back unless
    // the run commits.
    let forced = new Set<string>();
    let committed = false;
    try {
      // The id the pane already runs under is kept: it only changes by an identity answer or the server's adoption.
      // Read from the file on every catch-up, a read made as the user entered a cell could come back empty, and
      // minting an id there moved the pane to a workbook of its own — every open conversation turned read-only.
      this.workbookId = answered ?? this.workbookId ?? await this.deps.ensureWorkbookId();
      let workbookId = this.workbookId;
      const noFingerprints: Record<string, string | null> = {};
      // A read refused by a cell edit fails the run (it waits for the edit to end), never stands for "nothing":
      // no fingerprints would capture and upload every sheet again.
      const orElse = <T>(fallback: T) => (err: unknown): T => {
        if (isCellEditModeError(err)) throw err;
        return fallback;
      };
      // What this run answers for: the requests made from now on need another run (`queueRun`).
      this.pendingRun = false;
      forced = new Set(this.written);
      this.written.clear();
      const [{ workbookName, sheets }, names, fingerprints, documentUrl, iterative] = await Promise.all([
        this.deps.executor.listSheetsForIndex(),
        this.deps.executor.listDefinedNames().catch(orElse<Record<string, string>>({})),
        this.deps.executor.readSheetFingerprints ? this.deps.executor.readSheetFingerprints().catch(orElse(noFingerprints)) : Promise.resolve(noFingerprints),
        this.deps.executor.getDocumentUrl ? this.deps.executor.getDocumentUrl().catch(() => null) : Promise.resolve(null),
        this.deps.executor.readIterativeCalculation ? this.deps.executor.readIterativeCalculation().catch(() => null) : Promise.resolve(null),
      ]);
      if (!sheets.length) throw new Error('The workbook has no sheets.');
      if (this.rereadAll) {
        for (const sheet of sheets) forced.add(sheet.name);
        this.rereadAll = false;
      }
      const announced = sheets.map((s) => ({
        name: s.name,
        position: s.position,
        visibility: s.visibility,
        // A sheet we wrote in is announced without a fingerprint: it is captured again, always.
        fingerprint: forced.has(s.name) ? null : (fingerprints[s.name] ?? null),
      }));
      const writable = this.deps.writeWorkbookId ? await this.deps.writeWorkbookId(workbookId).catch(orElse(false)) : true;
      const job = await this.deps.api.startIndexJob(workbookId, {
        workbook_name: workbookName,
        sheets: announced,
        names,
        document_url: documentUrl,
        read_only: !writable,
        ...(dismissed ? { identity_ack: 'dismissed' as const } : {}),
        ...(this.picked ? { model: this.picked } : {}),
        ...(iterative ? { iterative_calculation: iterative } : {}),
      });
      if (!job.identity) this.picked = null;
      if (job.identity) {
        // Asked, not guessed (D1): the file is announced from a place its workbook does not know. Nothing is captured
        // and the chat waits — an index written under an id the answer may leave is wasted, and billed (D6).
        this.asked = { workbookId, documentUrl: documentUrl ?? '', name: workbookName, background };
        this.set({ phase: 'identity', identity: job.identity, ready: false, current: null, finishedAt: Date.now() });
        return;
      }
      // The server recognised this file as one it already holds (the id in the file was lost with a save that
      // never happened). Carry on under the id it knows: everything it has measured is still valid.
      if (job.workbook_id && job.workbook_id !== workbookId) {
        this.workbookId = workbookId = job.workbook_id;
        await this.deps.adoptWorkbookId?.(job.workbook_id).catch(() => undefined);
      }
      if (job.up_to_date) {
        // The server holds this exact workbook already: every sheet announced the fingerprint it has stored.
        // Nothing to capture, nothing to upload, nothing for it to rebuild — only the understanding to wait for,
        // which it restarts itself if the last pane was closed before the model had named every sheet.
        const status = await this.deps.api.getIndexStatus(workbookId);
        this.set({ total: 0, current: null });
        if (understood(status)) this.set({ phase: 'ready', ready: true, status, finishedAt: Date.now() });
        else this.set({ phase: 'understanding', ready: background, status });
        void this.watchUnderstanding(workbookId, status);
        return;
      }
      // Only the sheets the server asks for: the ones whose fingerprint it does not hold yet (all of them on a first
      // index, the changed ones on a re-index). Whole sheets, in order; the upload of a sheet overlaps the capture
      // of the next.
      const jobId = job.job_id as string;
      const wanted = new Set(job.expected);
      const toCapture = sheets.filter((s) => wanted.has(s.name));
      // Only a background catch-up gives way: a first index is one the user started and is watching.
      if (background) {
        this.capturing = new Set(toCapture.map((s) => s.name));
        this.superseded = false;
      }
      const dropped = (): boolean => {
        if (!this.superseded) return false;
        // Left open on the server: the next job start supersedes it. The run queued behind picks up both
        // this run's sheets (given back to `written` by the `finally` below) and the new change.
        this.set({ phase: this.state.status ? 'ready' : this.state.phase, ready: true, current: null, done: 0, total: 0 });
        return true;
      };
      const values = new Set(job.values_only ?? []);
      const fingerprintOf = new Map(announced.map((s) => [s.name, s.fingerprint]));
      this.set({ phase: 'capturing', total: toCapture.length });
      const report = (body: Parameters<NonNullable<IndexerDeps['api']['reportIndexEvent']>>[2]) => void this.deps.api.reportIndexEvent?.(workbookId, jobId, body);
      let pendingUpload: Promise<void> = Promise.resolve();
      let current: string | null = null;
      try {
        for (const [i, sheet] of toCapture.entries()) {
          if (this.stopped || dropped()) return;
          current = sheet.name;
          this.set({ current: sheet.name });
          report({ event: 'capture_start', sheet: sheet.name });
          const t0 = Date.now();
          // A sheet captured again only because it reads one that changed: its values alone, the server taking the
          // rest from the version before. Refused (that version does not hold its fingerprint), the whole sheet.
          const valuesOnly = values.has(sheet.name) && !!this.deps.executor.readSheetValuesForIndex;
          const payload = valuesOnly
            ? await this.deps.executor.readSheetValuesForIndex!(sheet.name, sheet.position, fingerprintOf.get(sheet.name) ?? null)
            : await this.deps.executor.readSheetForIndex(sheet.name, sheet.position);
          report({ event: 'capture_done', sheet: sheet.name, ms: Date.now() - t0, cells: payload.cells.length });
          await pendingUpload;
          const sent = this.upload(workbookId, jobId, payload).catch(async (err) => {
            if (!valuesOnly || !isValuesOnlyRefused(err)) throw err;
            await this.upload(workbookId, jobId, await this.deps.executor.readSheetForIndex(sheet.name, sheet.position));
          });
          pendingUpload = sent.then(() => this.set({ done: i + 1 }));
        }
        await pendingUpload;
      } catch (err) {
        report({ event: 'failed', sheet: current, error: err instanceof Error ? err.message : String(err) });
        throw err;
      }
      if (this.stopped || dropped()) return;
      this.set({ phase: 'committing', current: null });
      const status = await this.deps.api.commitIndexJob(workbookId, jobId);
      if (status.state !== 'ready') throw new Error(status.error || `Index state: ${status.state}`);
      committed = true;   // captured: the index holds them again
      if (understood(status)) this.set({ phase: 'ready', ready: true, status, finishedAt: Date.now() });
      else this.set({ phase: 'understanding', ready: background, status });
      void this.watchUnderstanding(workbookId, status);
    } catch (err) {
      if (background && isCellEditModeError(err)) {
        // The user is typing in a cell (a debounced change fired mid-edit): Excel answers no read until they
        // leave it. Not a failure: what was asked stays pending, and runs once the edit ends — Excel holds one
        // call until then, so no run is tried while the user types.
        this.pendingRun = true;
        this.set({ phase: this.state.status ? 'ready' : this.state.phase, ready: true, current: null });
        void this.waitForCellEditEnd().then(() => this.queueRun(), () => undefined);
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[sculpt] workbook index failed', err);
      this.set({ phase: 'failed', ready: background, error: message, finishedAt: Date.now() });
    } finally {
      if (!committed) for (const name of forced) this.written.add(name);
      this.capturing = null;
      this.superseded = false;
    }
  }

  private async upload(workbookId: string, jobId: string, payload: IndexSheetPayload | IndexSheetValues): Promise<void> {
    const t0 = Date.now();
    const encoded = await gzipJson(payload);
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
      try {
        await this.deps.api.uploadIndexSheet(workbookId, jobId, payload.name, encoded.body, encoded.encoding);
        void this.deps.api.reportIndexEvent?.(workbookId, jobId, { event: 'upload_done', sheet: payload.name, ms: Date.now() - t0, bytes: encoded.bytes });
        return;
      } catch (err) {
        lastError = err;
        if (isValuesOnlyRefused(err)) break;   // an answer, not a hiccup: asking again gets the same one
        if (attempt < UPLOAD_ATTEMPTS) await (this.deps.sleep ?? sleep)(500 * attempt);
      }
    }
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  /** The server's pass on `first.version`, watched by one loop per version: a newer version's loop replaces it
   *  (the old one stops at its next poll), and a turn refused for the pass joins it (`awaitUnderstood`). */
  private watchUnderstanding(workbookId: string, first: IndexStatus): Promise<void> {
    const loop = this.understand(workbookId, first).finally(() => {
      if (this.understanding === loop) this.understanding = null;
    });
    this.understanding = loop;
    return loop;
  }

  /** The understanding phase: poll the model pass until it finishes, and keep the composer locked until it
   *  does — **on the first version only**. A pass that stalls past `pollMaxMs` opens the chat with
   *  `backgroundUnderstanding` set.
   *
   *  Version 2 and later are almost always the agent's own writes coming back. The user watched them happen
   *  and what they want to say next is "undo that" or "no, 45 €/MWh"; holding the composer for eighty
   *  seconds while six sheets they just changed are re-described is not a trade worth making. The sheets
   *  that did not move keep their descriptions, the ones that did are there in rules form, and the pass
   *  finishes while the conversation carries on (the server answers those turns too — sculpt-api!13). */
  private async understand(workbookId: string, first: IndexStatus): Promise<void> {
    // A re-index's pass is a few seconds the user watches on the status line (and a message sent meanwhile waits
    // for): asked every second, not every four, the line stops less than a second after the server has finished.
    // Either cadence is reached from `POLL_FIRST_MS`, doubling: a pass that ends at once is seen at once, and one of
    // minutes costs three more asks than a fixed cadence.
    const cadence = this.deps.pollMs ?? (first.version > 1 ? REINDEX_POLL_MS : FIRST_INDEX_POLL_MS);
    let delay = this.deps.pollMs ?? Math.min(POLL_FIRST_MS, cadence);
    const pollMaxMs = this.deps.pollMaxMs ?? POLL_MAX_MS;
    const wait = this.deps.sleep ?? sleep;
    let status = first;
    const started = Date.now();
    if (first.version > 1 && !understood(first)) {
      this.set({ phase: 'ready', ready: true, backgroundUnderstanding: true, finishedAt: Date.now() });
    }
    while (!this.stopped && !understood(status) && Date.now() - started < pollMaxMs) {
      await wait(delay);
      delay = Math.min(delay * 2, cadence);
      if (this.stopped || this.state.status?.version !== first.version) return;
      try {
        status = await this.deps.api.getIndexStatus(workbookId);
      } catch {
        continue;
      }
      if (this.state.status?.version === status.version) this.set({ status });
    }
    if (!this.stopped && this.state.status?.version === first.version) {
      this.set({ phase: 'ready', ready: true, backgroundUnderstanding: !understood(status), status,
                 finishedAt: this.state.finishedAt ?? Date.now() });
    }
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** The server's 409 `values_only_refused` (`ApiError` shape: `status`, `body`): it does not hold the sheet's previous
 *  capture under this fingerprint, so the whole sheet is uploaded instead. */
function isValuesOnlyRefused(err: unknown): boolean {
  const e = err as { status?: unknown; body?: unknown } | null;
  return !!e && e.status === 409 && String(e.body ?? '').includes('values_only_refused');
}
